/**
 * @typedef {object} Metaobject
 * @property {string} handle - The metaobject's handle.
 * @property {Array<{key: string, value: string}>} fields - The fields of the metaobject.
 */

/**
 * @typedef {object} PageInfo
 * @property {boolean} hasNextPage - Whether there is a next page.
 * @property {boolean} hasPreviousPage - Whether there is a previous page.
 * @property {string} startCursor - The cursor for the start of the page.
 * @property {string} endCursor - The cursor for the end of the page.
 */

/**
 * @typedef {object} ApiData
 * @property {Metaobject[]} metaobjects - A list of metaobjects.
 * @property {PageInfo} pageInfo - Pagination information.
 */

/**
 * Manages sessionStorage caching with a Time-to-Live (TTL) strategy.
 * This prevents stale data and reduces redundant API calls.
 */
class MetaobjectCache {
  /**
   * @param {number} ttl - Time-to-live in milliseconds.
   * @param {string} storageKeyIndex - Key for the index of all cache keys.
   */
  constructor(ttl = 5 * 60 * 1000, storageKeyIndex = "metaobjects_cache_keys") {
    this.ttl = ttl;
    this.storageKeyIndex = storageKeyIndex;
    this.storage = sessionStorage;
  }

  /**
   * Retrieves an item from the cache if it exists and is not expired.
   * @param {string} key - The cache key.
   * @returns {any|null} The cached data or null if not found or expired.
   */
  get(key) {
    const cachedItemJSON = this.storage.getItem(key);
    if (!cachedItemJSON) return null;

    try {
      const { data, timestamp } = JSON.parse(cachedItemJSON);
      if (Date.now() - timestamp > this.ttl) {
        this.remove(key);
        return null;
      }
      return data;
    } catch (error) {
      this.remove(key); // Remove corrupted data
      return null;
    }
  }

  /**
   * Adds or updates an item in the cache with a timestamp.
   * @param {string} key - The cache key.
   * @param {any} data - The data to store.
   */
  set(key, data) {
    const itemToCache = { data, timestamp: Date.now() };
    try {
      this.storage.setItem(key, JSON.stringify(itemToCache));
      this._addToIndex(key);
    } catch (error) {
      if (error.name === "QuotaExceededError") {
        this.clear();
        try {
          // Retry setting the item after clearing the cache
          this.storage.setItem(key, JSON.stringify(itemToCache));
          this._addToIndex(key);
        } catch (error2) {
          // Do nothing
        }
      } else {
        // Do nothing
      }
    }
  }

  /**
   * Removes a specific item from the cache and its index.
   * @param {string} key - The cache key to remove.
   */
  remove(key) {
    this.storage.removeItem(key);
    const keys = this._getIndex();
    const updatedKeys = keys.filter((k) => k !== key);
    this.storage.setItem(this.storageKeyIndex, JSON.stringify(updatedKeys));
  }

  /**
   * Clears all cache entries managed by this instance.
   */
  clear() {
    const keys = this._getIndex();
    keys.forEach((key) => this.storage.removeItem(key));
    this.storage.removeItem(this.storageKeyIndex);
  }

  /**
   * Adds a key to the cache index.
   * @private
   * @param {string} key - The key to add.
   */
  _addToIndex(key) {
    const keys = this._getIndex();
    if (!keys.includes(key)) {
      keys.push(key);
      this.storage.setItem(this.storageKeyIndex, JSON.stringify(keys));
    }
  }

  /**
   * Retrieves the cache index.
   * @private
   * @returns {string[]} An array of cache keys.
   */
  _getIndex() {
    try {
      return JSON.parse(this.storage.getItem(this.storageKeyIndex) || "[]");
    } catch (error) {
      this.storage.removeItem(this.storageKeyIndex);
      return [];
    }
  }
}

/**
 * Handles all interactions with the Metaobject API.
 */
class MetaobjectAPI {
  /**
   * @param {string} metaobjectType - The type of metaobject to fetch.
   * @param {string} baseApi - The base URL for the API.
   */
  constructor(metaobjectType, baseApi) {
    if (!baseApi) {
      throw new Error("MetaobjectAPI requires a baseApi URL");
    }
    this.baseApiUrl = `${baseApi}metaobjects/${metaobjectType}`;
  }

  /**
   * Builds the full API URL with query parameters.
   * @param {object} state - The current state of filters, sorting, and pagination.
   * @returns {string} The complete API URL.
   */
  buildApiUrl({ sort, filters, pagination }) {
    const urlParams = new URLSearchParams();
    const { key: sortKey, reverse } = sort;
    if (sortKey && reverse !== undefined) {
      urlParams.append("sortKey", sortKey);
      urlParams.append("reverse", String(reverse));
    }

    const queryParts = Object.entries(filters)
      .map(([key, { values, logic }]) => {
        if (values.length === 0) return null;
        const individualQueries = values.map((value) => {
          const escapedValue = value
            .replace(/\\/g, "\\\\")
            .replace(/'/g, "\\'");
          return `fields.${key}:'${escapedValue}'`;
        });
        const joiner = logic === "AND" ? " AND " : " OR ";
        let groupQuery = individualQueries.join(joiner);
        if (individualQueries.length > 1) {
          groupQuery = `(${groupQuery})`;
        }
        return groupQuery;
      })
      .filter(Boolean);

    if (queryParts.length > 0) {
      urlParams.append("query", queryParts.join(" AND "));
    }

    Object.entries(pagination).forEach(([key, value]) => {
      if (value) urlParams.append(key, value);
    });

    return `${this.baseApiUrl}?${urlParams.toString()}`;
  }

  /**
   * Fetches metaobjects from the API.
   * @param {string} url - The URL to fetch from.
   * @param {AbortSignal} signal - The AbortSignal to allow request cancellation.
   * @returns {Promise<ApiData>} The data from the API.
   */
  async fetch(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }
}

/**
 * Manages all DOM interactions and updates.
 */
class MetaobjectUI {
  constructor({ settings, selectors, translations }) {
    this.settings = settings;
    this.selectors = selectors;
    this.translations = translations;
    this.elements = this._queryElements();
    this._setupMobileFilterContainer();
  }

  /**
   * Queries and stores references to all necessary DOM elements.
   * @private
   */
  _queryElements() {
    const elements = {};
    for (const [key, selector] of Object.entries(this.selectors)) {
      elements[key] = document.querySelector(selector);
    }
    return elements;
  }

  /**
   * Creates and injects the mobile filter container into the DOM.
   * @private
   */
  _setupMobileFilterContainer() {
    if (!this.elements.mainContainer) return;

    this.elements.mobileFilterContainer = document.createElement("div");
    this.elements.mobileFilterContainer.className =
      "metaobject-grid__filter-container--mobile";

    const mainContent = document.querySelector("#MainContent");
    if (mainContent?.parentNode) {
      mainContent.parentNode.insertBefore(
        this.elements.mobileFilterContainer,
        mainContent.nextSibling
      );
    } else {
      document.body.appendChild(this.elements.mobileFilterContainer);
    }
  }

  /**
   * Renders skeleton loaders for cards and pagination.
   */
  showSkeletons() {
    const { cardsContainer, paginationContainer } = this.elements;
    if (!cardsContainer) return;

    const cardFragment = document.createDocumentFragment();
    for (let i = 0; i < this.settings.items_per_page; i++) {
      const card = document.createElement("div");
      card.className = "skeleton-card";
      card.innerHTML = `
                <div class="skeleton-item skeleton-image"></div>
                <div class="skeleton-item skeleton-title"></div>
                <div class="skeleton-item skeleton-button"></div>
            `;
      cardFragment.appendChild(card);
    }
    cardsContainer.replaceChildren(cardFragment);

    if (paginationContainer) {
      paginationContainer.innerHTML = `
                <div class="skeleton-pagination-button"></div>
                <div class="skeleton-pagination-button"></div>
            `;
    }
  }

  /**
   * Displays an error message in the grid.
   * @param {string} message - The error message to display.
   */
  renderError() {
    const { cardsContainer, paginationContainer } = this.elements;
    if (cardsContainer) {
      const p = document.createElement("p");
      p.textContent = this.translations.error_loading;
      cardsContainer.replaceChildren(p);
    }
    if (paginationContainer) {
      paginationContainer.innerHTML = "";
    }
  }

  /**
   * Renders the metaobject cards.
   * @param {Metaobject[]} metaobjects - The metaobjects to display.
   */
  renderMetaobjects(metaobjects) {
    const { cardsContainer } = this.elements;
    if (!cardsContainer) return;

    if (!metaobjects || metaobjects.length === 0) {
      cardsContainer.innerHTML = `<p>${this.translations.no_items_found}</p>`;
      return;
    }

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < metaobjects.length; i++) {
      const card = this._createCardElement(metaobjects[i], i);
      fragment.appendChild(card);
    }
    cardsContainer.replaceChildren(fragment);
  }

  /**
   * Creates a single card element for a metaobject.
   * @private
   * @param {Metaobject} metaobject - The metaobject data.
   * @param {number} [index=0] - Index of the card (for eager/lazy loading).
   * @returns {HTMLAnchorElement} The created card element.
   */
  _createCardElement(metaobject, index = 0) {
    const title =
      metaobject.fields.find((f) => f.key === "title")?.value ||
      this.translations.no_title;
    const url = `${this.settings.base_page_url}/${metaobject.handle}`;
    const image = metaobject.fields.find((f) => f.key === "gallery")?.references
      ?.nodes?.[0]?.image;

    const card = document.createElement("a");
    card.href = url;
    card.className = "ff-card";
    card.setAttribute(
      "aria-label",
      this.translations.view_details_for.replace("[title]", title)
    );

    const imageEl = this._createImageElement(image, title, index);

    const cardContent = document.createElement("div");
    cardContent.className = "ff-card__content";

    const h3 = document.createElement("h3");
    h3.className = "ff-card__title";
    h3.textContent = title;

    const span = document.createElement("span");
    span.className =
      "ff-button ff-button--secondary ff-button--sharp ff-button--small ff-component";
    span.textContent = this.translations.learn_more;

    cardContent.append(h3, span);

    card.append(imageEl, cardContent);
    return card;
  }

  /**
   * Creates an image element or a placeholder.
   * Uses Shopify CDN width param for optimized size; first 4 images load eagerly.
   * @private
   * @param {object|undefined} image - The image object from the metaobject.
   * @param {string} altText - The alt text for the image.
   * @param {number} [index=0] - Index of the image (for eager/lazy and fetchpriority).
   * @returns {HTMLImageElement|HTMLDivElement}
   */
  _createImageElement(image, altText, index = 0) {
    if (image?.url) {
      const img = document.createElement("img");
      img.className = "ff-card__image";

      const cardWidth = 400;
      const separator = image.url.includes("?") ? "&" : "?";
      img.src = `${image.url}${separator}width=${cardWidth}&format=webp`;

      const widths = [200, 400, 600];
      img.srcset = widths
        .map((w) => `${image.url}${separator}width=${w}&format=webp ${w}w`)
        .join(", ");
      img.sizes =
        "(max-width: 600px) 200px, (max-width: 1024px) 400px, 400px";

      img.loading = index < 4 ? "eager" : "lazy";
      if (index < 4) {
        img.fetchPriority = "high";
      }

      img.width = cardWidth;
      if (image.width && image.height) {
        img.height = Math.round((cardWidth / image.width) * image.height);
      }

      img.alt = altText;
      return img;
    } else {
      const placeholder = document.createElement("div");
      placeholder.className = "ff-card__image placeholder-svg";
      placeholder.setAttribute("role", "img");
      placeholder.setAttribute(
        "aria-label",
        this.translations.placeholder_image
      );
      return placeholder;
    }
  }

  /**
   * Renders pagination buttons.
   * @param {PageInfo} pageInfo - Pagination information.
   */
  renderPagination(pageInfo) {
    const { paginationContainer } = this.elements;
    if (!paginationContainer) return;
    if (!pageInfo) {
      paginationContainer.innerHTML = "";
      return;
    }

    const prevButton = this._createPaginationButton(
      this.translations.previous,
      !pageInfo.hasPreviousPage,
      "prev",
      pageInfo.startCursor
    );
    const nextButton = this._createPaginationButton(
      this.translations.next,
      !pageInfo.hasNextPage,
      "next",
      pageInfo.endCursor
    );

    paginationContainer.replaceChildren(prevButton, nextButton);
  }

  /**
   * Helper to create a pagination button.
   * @private
   */
  _createPaginationButton(text, disabled, page, cursor) {
    const button = document.createElement("button");
    button.innerText = text;
    button.disabled = disabled;
    button.dataset.page = page;
    if (cursor) button.dataset.cursor = cursor;
    button.className = `ff-button ${disabled ? "ff-button--disabled" : "ff-button--secondary"}`;
    return button;
  }

  /**
   * Renders the filter options.
   * @param {Map<string, Map<string, number>>} filterOptions - Available options for filters.
   */
  renderFilters(filterOptions) {
    const containers = [
      this.elements.filterContainer,
      this.elements.mobileFilterContainer,
    ].filter(Boolean);
    if (containers.length === 0) return;

    const fragment = document.createDocumentFragment();

    const header = this._createFilterHeader();
    const { content, filterGroupsCount } =
      this._createFilterContent(filterOptions);

    fragment.append(header, content);

    containers.forEach((container, index) => {
      const contentToAppend =
        index < containers.length - 1 ? fragment.cloneNode(true) : fragment;
      container.replaceChildren(contentToAppend);
      if (filterGroupsCount === 0) {
        container.classList.add("hidden");
      } else {
        container.classList.remove("hidden");
      }
    });
  }

  _createFilterHeader() {
    const header = document.createElement("div");
    header.className = "metaobject-filter-header";
    const closeButton = document.createElement("button");
    closeButton.className = "ff-sidebar-close";
    closeButton.setAttribute("aria-label", this.translations.close_filters);
    closeButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true"><path d="M 38.982422 6.9707031 A 2.0002 2.0002 0 0 0 37.585938 7.5859375 L 24 21.171875 L 10.414062 7.5859375 A 2.0002 2.0002 0 0 0 8.9785156 6.9804688 A 2.0002 2.0002 0 0 0 7.5859375 10.414062 L 21.171875 24 L 7.5859375 37.585938 A 2.0002 2.0002 0 1 0 10.414062 40.414062 L 24 26.828125 L 37.585938 40.414062 A 2.0002 2.0002 0 1 0 40.414062 37.585938 L 26.828125 24 L 40.414062 10.414062 A 2.0002 2.0002 0 0 0 38.982422 6.9707031 z"></path></svg>`;

    header.appendChild(closeButton);
    return header;
  }

  _createFilterContent(filterOptions) {
    const content = document.createElement("div");
    content.className = "metaobject-filter-content";
    let filterGroupsCount = 0;

    this.settings.filters.forEach((filterSetting) => {
      const key = filterSetting.field_key;
      const optionsMap = filterOptions.get(key);
      if (!optionsMap || optionsMap.size === 0) return;

      const groupDiv = this._createFilterGroup(filterSetting, optionsMap);
      content.appendChild(groupDiv);
      filterGroupsCount++;
    });

    return { content, filterGroupsCount };
  }

  _createFilterGroup(filterSetting, optionsMap) {
    const { field_key: key, filter_logic: logic, label } = filterSetting;
    const options = [...optionsMap.keys()].sort();
    const icon = `<i class="icon-chevron"><svg style="width:24px;height:24px;stroke-width:0px;" viewBox="0 0 24 24"><path fill="#000000" d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"></path></svg></i>`;

    const groupDiv = document.createElement("div");
    groupDiv.className = "metaobject-filter-group";
    groupDiv.dataset.filterKey = key;
    groupDiv.dataset.filterLogic = logic;

    const header = document.createElement("header");
    const toggleBtn = document.createElement("button");
    toggleBtn.type = "button";
    toggleBtn.className = "filter-header";
    toggleBtn.setAttribute("aria-expanded", "true");
    const ulId = `filter-options-${key}`;
    toggleBtn.setAttribute("aria-controls", ulId);
    const labelSpan = document.createElement("span");
    labelSpan.textContent = label || key;
    toggleBtn.appendChild(labelSpan);
    toggleBtn.insertAdjacentHTML("beforeend", icon);
    const iconEl = toggleBtn.querySelector(".icon-chevron");
    if (iconEl) iconEl.setAttribute("aria-hidden", "true");
    header.appendChild(toggleBtn);

    const ul = document.createElement("ul");
    ul.className = "filter-options";
    ul.id = ulId;

    options.forEach((option, index) => {
      const li = this._createFilterOption(
        key,
        option,
        optionsMap.get(option),
        index
      );
      ul.appendChild(li);
    });

    groupDiv.append(header, ul);

    if (options.length > 10) {
      const button = document.createElement("button");
      const buttonIcon = `<svg class="icon icon-show-more" height="512" viewBox="0 0 512 512" width="512" xmlns="http://www.w3.org/2000/svg"><path d="m192 424a23.928 23.928 0 0 1 -16.971-7.029l-144-144a24 24 0 0 1 0-33.942l144-144a24 24 0 0 1 33.942 33.942l-127.03 127.029 127.03 127.029a24 24 0 0 1 -16.971 40.971zm144.971-7.029 144-144a24 24 0 0 0 0-33.942l-144-144a24 24 0 0 0 -33.942 33.942l127.03 127.029-127.03 127.029a24 24 0 0 0 33.942 33.942z"></path></svg>`;
      button.type = "button";
      button.className = "show-more-less-btn";
      button.innerHTML = `${buttonIcon}${this.translations.show_more}`;
      groupDiv.appendChild(button);
    }
    return groupDiv;
  }

  _createFilterOption(key, option, count, index) {
    const li = document.createElement("li");
    if (index >= 10) li.className = "filter-option-extra hidden";

    const label = document.createElement("label");
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.name = key;
    checkbox.value = option;
    label.append(checkbox);

    if (key.includes("color")) {
      const sanitizedName = option
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const colorSwatch = document.createElement("span");
      colorSwatch.className = "color-swatch";
      colorSwatch.style.backgroundColor = `var(--ff-color-${sanitizedName}, ${sanitizedName})`;
      label.append(colorSwatch);
    }

    label.append(`${option} (${count})`);
    li.appendChild(label);
    return li;
  }

  /**
   * Syncs the checked state of a checkbox between filter containers.
   * @param {HTMLInputElement} targetCheckbox - The checkbox that was changed.
   */
  syncCheckbox(targetCheckbox) {
    const { name, value, checked } = targetCheckbox;
    const isInMobile = targetCheckbox.closest(
      ".metaobject-grid__filter-container--mobile"
    );
    const syncContainer = isInMobile
      ? this.elements.filterContainer
      : this.elements.mobileFilterContainer;

    if (syncContainer) {
      const escapedName = CSS.escape(name);
      const escapedValue = CSS.escape(value);
      const syncCheckbox = syncContainer.querySelector(
        `input[name="${escapedName}"][value="${escapedValue}"]`
      );
      if (syncCheckbox && syncCheckbox.checked !== checked) {
        syncCheckbox.checked = checked;
      }
    }
  }

  /**
   * Sets the checked state of filters from a state object.
   * @param {object} filterState - The current state of filters.
   */
  setCheckedFilters(filterState) {
    const containers = [
      this.elements.filterContainer,
      this.elements.mobileFilterContainer,
    ].filter(Boolean);
    if (containers.length === 0) return;

    // First, uncheck all
    containers.forEach((c) =>
      c
        .querySelectorAll('input[type="checkbox"]')
        .forEach((cb) => (cb.checked = false))
    );

    // Then, check the ones in the state
    Object.entries(filterState).forEach(([name, { values }]) => {
      values.forEach((value) => {
        containers.forEach((container) => {
          const escapedName = CSS.escape(name);
          const escapedValue = CSS.escape(value);
          const input = container.querySelector(
            `input[name="${escapedName}"][value="${escapedValue}"]`
          );
          if (input) input.checked = true;
        });
      });
    });
  }
}

/**
 * The main controller for the Metaobject Grid.
 * Orchestrates the API, UI, and Cache classes.
 */
class MetaobjectGridController {
  /**
   * @param {HTMLElement} container - The main container element for the grid.
   */
  constructor(container) {
    this.container = container;
    try {
      this.settings = JSON.parse(container.dataset.settings);
    } catch (error) {
      return;
    }

    this.translations = this.settings.translations;
    this.ui = new MetaobjectUI({
      settings: this.settings,
      translations: this.translations,
      selectors: {
        mainContainer: ".metaobject-grid-main",
        cardsContainer: ".metaobject-grid__grid",
        paginationContainer: ".pagination-container",
        filterToggle: ".metaobject-grid__toggle-filter",
        sortBySelect: "#sort-by",
        filterContainer: ".metaobject-grid__filter-container",
      },
    });

    this.api = new MetaobjectAPI(
      this.settings.metaobject_type,
      window.theme_api_url
    );
    this.cache = new MetaobjectCache();

    this.state = this._getInitialState();
    this.sortTimeout = null;

    this.metaobjectsAbortController = null;

    // Bind methods to maintain 'this' context
    this._handleFilterClick = this._handleFilterClick.bind(this);
    this._handleFilterChange = this._handleFilterChange.bind(this);
    this._handlePaginationClick = this._handlePaginationClick.bind(this);
    this._handleSortChange = this._handleSortChange.bind(this);
    this._handleGlobalClick = this._handleGlobalClick.bind(this);

    this._bindEvents();
    this._init();
  }

  /**
   * Base64-encodes a string safely for cache keys (handles Unicode).
   * @private
   * @param {string} str - URL or string that may contain non-ASCII characters.
   * @returns {string}
   */
  _safeBtoa(str) {
    return btoa(unescape(encodeURIComponent(str)));
  }

  /**
   * Gets the initial state from the DOM (e.g., sort dropdown).
   * @private
   */
  _getInitialState() {
    return {
      sort: this._getSortState(),
      filters: {},
      pagination: {
        first: this.settings.items_per_page,
      },
    };
  }

  /**
   * Binds all necessary event listeners.
   * @private
   */
  _bindEvents() {
    const {
      filterContainer,
      mobileFilterContainer,
      paginationContainer,
      sortBySelect,
      filterToggle,
    } = this.ui.elements;

    [filterContainer, mobileFilterContainer]
      .filter(Boolean)
      .forEach((container) => {
        container.addEventListener("click", this._handleFilterClick);
        container.addEventListener("change", this._handleFilterChange);
      });

    if (paginationContainer) {
      paginationContainer.addEventListener(
        "click",
        this._handlePaginationClick
      );
    }

    if (sortBySelect) {
      sortBySelect.addEventListener("change", this._handleSortChange);
    }

    if (filterToggle) {
      this._handleFilterToggle = () =>
        document.body.classList.toggle("open_filter_mobile");
      filterToggle.addEventListener("click", this._handleFilterToggle);
    }

    // Global listener for closing the mobile filter overlay
    document.addEventListener("click", this._handleGlobalClick);
  }

  /**
   * Handles global click events for closing mobile filter overlay.
   * @private
   */
  _handleGlobalClick(e) {
    if (
      e.target.closest(".background-overlay") ||
      e.target.closest(".ff-sidebar-close")
    ) {
      document.body.classList.remove("open_filter_mobile");
    }
  }

  /**
   * Kicks off the initial data fetch (unified: one request for cards + filters).
   * @private
   */
  async _init() {
    const { staleData, freshDataPromise } = this.fetchUnifiedDataSWR();

    if (staleData) {
      this._renderUnifiedData(staleData, undefined);
      freshDataPromise
        .then((freshData) => this._renderUnifiedData(freshData, undefined))
        .catch(() => { });
    } else {
      this.ui.showSkeletons();
      try {
        const allData = await freshDataPromise;
        this._renderUnifiedData(allData, undefined);
      } catch (error) {
        if (error.name !== "AbortError") {
          this.ui.renderError();
        }
      }
    }
  }

  /**
   * Builds pageInfo for the displayed slice (items_per_page), not the full
   * filter_limit set. Uses cursor on each metaobject when provided by backend.
   * When per-item cursors are absent, does not use apiPageInfo.endCursor (it
   * represents the end of the filter_limit window) to avoid skipping items.
   * @private
   * @param {Metaobject[]} allMetaobjects - All fetched metaobjects.
   * @param {PageInfo} apiPageInfo - pageInfo from the API response.
   * @returns {PageInfo}
   */
  _buildDisplayPageInfo(allMetaobjects, apiPageInfo) {
    const displayCount = Math.min(
      allMetaobjects.length,
      this.settings.items_per_page
    );
    const hasMore =
      allMetaobjects.length > displayCount || apiPageInfo?.hasNextPage;

    const lastDisplayed = displayCount > 0 ? allMetaobjects[displayCount - 1] : null;
    const hasSafeEndCursor = lastDisplayed?.cursor != null;
    const endCursor = hasSafeEndCursor ? lastDisplayed.cursor : null;

    return {
      hasNextPage: hasMore && hasSafeEndCursor,
      hasPreviousPage: apiPageInfo?.hasPreviousPage ?? false,
      startCursor: apiPageInfo?.startCursor ?? null,
      endCursor,
    };
  }

  /**
   * Renders unified data (cards + pagination + filters). Used by init, filter,
   * and sort to avoid duplicated display logic.
   * @private
   * @param {ApiData} allData - Full API response (metaobjects + pageInfo).
   * @param {EventTarget|null} [eventTarget] - Optional filter event target for _getFilterState context.
   */
  _renderUnifiedData(allData, eventTarget) {
    const metaobjects = allData?.metaobjects ?? [];
    const displayPageInfo = this._buildDisplayPageInfo(
      metaobjects,
      allData?.pageInfo
    );
    const displayData = {
      metaobjects: metaobjects.slice(0, this.settings.items_per_page),
      pageInfo: displayPageInfo,
    };
    this.renderMetaobjectsUI(displayData);
    if (!eventTarget || displayData.metaobjects.length !== 0) {
      this.renderFiltersUI(metaobjects, eventTarget);
    }
  }

  /**
   * Normalizes API response: ensures metaobjects is an array and filters out DRAFT.
   * Single place for response shape validation and filter logic (DRY, defensive).
   * @private
   * @param {object} data - Raw API response.
   * @returns {ApiData}
   */
  _normalizeMetaobjectsResponse(data) {
    const metaobjects = data?.metaobjects ?? [];
    const filteredMetaobjects = metaobjects.filter(
      (m) => m.capabilities?.publishable?.status !== "DRAFT"
    );
    return { ...data, metaobjects: filteredMetaobjects };
  }

  /**
   * Fetches a single dataset for both card display and filter generation.
   * Always requests filter_limit items; caller slices for display.
   * @returns {Promise<ApiData>}
   */
  async fetchUnifiedData() {
    if (this.metaobjectsAbortController) {
      this.metaobjectsAbortController.abort();
    }
    this.metaobjectsAbortController = new AbortController();
    const signal = this.metaobjectsAbortController.signal;

    const unifiedState = {
      ...this.state,
      pagination: { first: this.settings.filter_limit },
    };
    const url = this.api.buildApiUrl(unifiedState);
    const cacheKey = `metaobjects_unified_${this._safeBtoa(url)}`;

    const cachedData = this.cache.get(cacheKey);
    if (cachedData) return cachedData;

    const data = await this.api.fetch(url, signal);
    const result = this._normalizeMetaobjectsResponse(data);
    this.cache.set(cacheKey, result);
    return result;
  }

  /**
   * Stale-while-revalidate: returns cached data (if any) immediately and
   * always kicks off a fresh network request. Caller renders stale then
   * revalidates with the promise.
   * @returns {{ staleData?: ApiData, freshDataPromise: Promise<ApiData> }}
   */
  fetchUnifiedDataSWR() {
    const unifiedState = {
      ...this.state,
      pagination: { first: this.settings.filter_limit },
    };
    const url = this.api.buildApiUrl(unifiedState);
    const cacheKey = `metaobjects_unified_${this._safeBtoa(url)}`;

    const staleData = this.cache.get(cacheKey);

    if (this.metaobjectsAbortController) {
      this.metaobjectsAbortController.abort();
    }
    this.metaobjectsAbortController = new AbortController();
    const signal = this.metaobjectsAbortController.signal;

    const freshDataPromise = this.api
      .fetch(url, signal)
      .then((data) => {
        const result = this._normalizeMetaobjectsResponse(data);
        this.cache.set(cacheKey, result);
        return result;
      });

    return { staleData, freshDataPromise };
  }

  /**
   * Fetches metaobject data from cache or API.
   * @param {object} paginationOverride - Optional pagination parameters.
   * @returns {Promise<ApiData|null>}
   */
  async fetchMetaobjectsData(paginationOverride = {}) {
    if (this.metaobjectsAbortController) {
      this.metaobjectsAbortController.abort();
    }
    this.metaobjectsAbortController = new AbortController();
    const signal = this.metaobjectsAbortController.signal;

    this.state.pagination =
      Object.keys(paginationOverride).length > 0
        ? paginationOverride
        : this._getInitialState().pagination;
    const url = this.api.buildApiUrl(this.state);
    const cacheKey = `metaobjects_data_${this._safeBtoa(url)}`;

    const cachedData = this.cache.get(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    try {
      const data = await this.api.fetch(url, signal);
      const filteredData = this._normalizeMetaobjectsResponse(data);
      this.cache.set(cacheKey, filteredData);
      return filteredData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Renders the metaobjects and pagination UI.
   * @param {ApiData} data - The data to render.
   */
  renderMetaobjectsUI(data) {
    if (!data) return;
    this.ui.renderMetaobjects(data.metaobjects);
    this.ui.renderPagination(data.pageInfo);
  }

  /**
   * Orchestrates fetching and rendering of metaobjects.
   * @param {object} paginationOverride - Optional pagination parameters.
   */
  async fetchAndRenderMetaobjects(paginationOverride = {}, showSkeletons = true) {
    if (showSkeletons) {
      this.ui.showSkeletons();
    }
    try {
      const data = await this.fetchMetaobjectsData(paginationOverride);
      this.renderMetaobjectsUI(data);
    } catch (error) {
      if (error.name !== "AbortError") {
        this.ui.renderError();
      }
    }
  }

  /**
   * Renders the filters UI.
   * Uses this.state.filters when eventTarget is detached (e.g. after re-render)
   * so checked state is preserved (defensive).
   * @param {Metaobject[]} metaobjectsForFilters - Data to build filter options.
   * @param {EventTarget|null} [eventTarget] - Optional; used to read filter state from DOM.
   */
  renderFiltersUI(metaobjectsForFilters, eventTarget) {
    if (!metaobjectsForFilters) return;
    const isDetached =
      eventTarget && typeof document !== "undefined" && !document.body.contains(eventTarget);
    const filterStateBeforeFetch = isDetached
      ? this.state.filters
      : this._getFilterState(eventTarget);
    const filterOptions = this._getFilterOptions(metaobjectsForFilters);
    this.ui.renderFilters(filterOptions);
    this.ui.setCheckedFilters(filterStateBeforeFetch);
  }

  /**
   * Extracts available filter options from a list of metaobjects.
   * @private
   * @param {Metaobject[]} data - The metaobjects to process.
   * @returns {Map<string, Map<string, number>>}
   */
  _getFilterOptions(data) {
    if (!data) return new Map();
    const filterOptions = new Map();
    const filterKeys = this.settings.filters.map((f) => f.field_key);
    filterKeys.forEach((key) => filterOptions.set(key, new Map()));

    for (const metaobject of data) {
      const source = metaobject.fields.reduce(
        (acc, { key, value }) => ({ ...acc, [key]: value }),
        {}
      );
      for (const key of filterKeys) {
        if (!source[key]) continue;
        try {
          const values =
            typeof source[key] === "string"
              ? JSON.parse(source[key])
              : source[key];
          if (Array.isArray(values)) {
            const uniqueValues = [...new Set(values)];
            uniqueValues.forEach((value) => {
              const optionsMap = filterOptions.get(key);
              optionsMap.set(value, (optionsMap.get(value) || 0) + 1);
            });
          }
        } catch (error) {
          // Do nothing
        }
      }
    }
    return filterOptions;
  }

  /**
   * Event handler for clicks within the filter containers (delegated).
   * @private
   */
  _handleFilterClick(e) {
    const toggle = e.target.closest(".filter-header");
    if (toggle) {
      const group = toggle.closest(".metaobject-filter-group");
      if (group) {
        group.classList.toggle("collapsed");
        const expanded = !group.classList.contains("collapsed");
        toggle.setAttribute("aria-expanded", String(expanded));
      }
      return;
    }

    const showMoreBtn = e.target.closest(".show-more-less-btn");
    if (showMoreBtn) {
      const icon = `<svg class="icon icon-show-more" height="512" viewBox="0 0 512 512" width="512" xmlns="http://www.w3.org/2000/svg"><path d="m192 424a23.928 23.928 0 0 1 -16.971-7.029l-144-144a24 24 0 0 1 0-33.942l144-144a24 24 0 0 1 33.942 33.942l-127.03 127.029 127.03 127.029a24 24 0 0 1 -16.971 40.971zm144.971-7.029 144-144a24 24 0 0 0 0-33.942l-144-144a24 24 0 0 0 -33.942 33.942l127.03 127.029-127.03 127.029a24 24 0 0 0 33.942 33.942z"></path></svg>`;
      const group = showMoreBtn.closest(".metaobject-filter-group");
      const extraItems = group.querySelectorAll(".filter-option-extra");
      const isShowingMore =
        showMoreBtn.textContent === this.translations.show_less;
      extraItems.forEach((item) => item.classList.toggle("hidden"));
      showMoreBtn.innerHTML = isShowingMore
        ? `${icon}${this.translations.show_more}`
        : `${icon}${this.translations.show_less}`;
    }
  }

  /**
   * Event handler for checkbox changes in filters.
   * @private
   */
  async _handleFilterChange(e) {
    const checkbox = e.target;
    if (checkbox.matches('input[type="checkbox"]')) {
      this.ui.syncCheckbox(checkbox);
      this.state.filters = this._getFilterState(e.target);

      const { staleData, freshDataPromise } = this.fetchUnifiedDataSWR();

      if (staleData) {
        this._renderUnifiedData(staleData, e.target);
        // Pass undefined on revalidation so filter state is read from current DOM (e.target may be detached after first render).
        freshDataPromise
          .then((freshData) => this._renderUnifiedData(freshData, undefined))
          .catch(() => {
            /* Revalidation failed; keep stale UI visible. */
          });
      } else {
        this.ui.showSkeletons();
        try {
          const allData = await freshDataPromise;
          this._renderUnifiedData(allData, e.target);
        } catch (error) {
          if (error.name !== "AbortError") {
            this.ui.renderError();
          }
        }
      }
    }
  }

  /**
   * Reads the current filter state from the DOM.
   * @returns {object} The current filter state.
   */
  _getFilterState(eventTarget) {
    const filterState = {};

    let container = this.ui.elements.filterContainer;
    if (eventTarget) {
      container = eventTarget.closest(
        ".metaobject-grid__filter-container, .metaobject-grid__filter-container--mobile"
      );
    }

    if (!container) return filterState;

    container.querySelectorAll(".metaobject-filter-group").forEach((group) => {
      const key = group.dataset.filterKey;
      const logic = group.dataset.filterLogic;
      const checkedInputs = group.querySelectorAll(
        'input[type="checkbox"]:checked'
      );

      filterState[key] = {
        values: Array.from(checkedInputs).map((cb) => cb.value),
        logic: logic,
      };
    });

    return filterState;
  }

  /**
   * Event handler for pagination button clicks.
   * @private
   */
  _handlePaginationClick(e) {
    const button = e.target.closest("button");
    if (!button || button.disabled) return;

    const { page, cursor } = button.dataset;
    const pagination = {};
    if (page === "next") {
      pagination.first = this.settings.items_per_page;
      pagination.after = cursor;
    } else if (page === "prev") {
      pagination.last = this.settings.items_per_page;
      pagination.before = cursor;
    }
    this.fetchAndRenderMetaobjects(pagination);
  }

  /**
   * Reads sort state from the DOM.
   * @private
   */
  _getSortState() {
    if (!this.ui.elements.sortBySelect) {
      return {};
    }
    const selectedSort = this.ui.elements.sortBySelect.value;
    const sortMap = {
      updated_at_desc: { key: "updated_at", reverse: true },
      updated_at_asc: { key: "updated_at", reverse: false },
    };
    return sortMap[selectedSort] || {};
  }

  /**
   * Event handler for the sort dropdown change.
   * @private
   */
  _handleSortChange() {
    clearTimeout(this.sortTimeout);
    this.sortTimeout = setTimeout(async () => {
      this.state.sort = this._getSortState();

      const { staleData, freshDataPromise } = this.fetchUnifiedDataSWR();

      if (staleData) {
        this._renderUnifiedData(staleData, undefined);
        freshDataPromise
          .then((freshData) => this._renderUnifiedData(freshData, undefined))
          .catch(() => { });
      } else {
        this.ui.showSkeletons();
        try {
          const allData = await freshDataPromise;
          this._renderUnifiedData(allData, undefined);
        } catch (error) {
          if (error.name !== "AbortError") {
            this.ui.renderError();
          }
        }
      }
    }, 300); // Debounce to prevent rapid firing
  }

  /**
   * Removes all event listeners and cleans up resources.
   * Call this before removing the grid from the DOM.
   */
  destroy() {
    const {
      filterContainer,
      mobileFilterContainer,
      paginationContainer,
      sortBySelect,
      filterToggle,
    } = this.ui.elements;

    [filterContainer, mobileFilterContainer]
      .filter(Boolean)
      .forEach((container) => {
        container.removeEventListener("click", this._handleFilterClick);
        container.removeEventListener("change", this._handleFilterChange);
      });

    if (paginationContainer) {
      paginationContainer.removeEventListener(
        "click",
        this._handlePaginationClick
      );
    }

    if (sortBySelect) {
      sortBySelect.removeEventListener("change", this._handleSortChange);
    }

    if (filterToggle && this._handleFilterToggle) {
      filterToggle.removeEventListener("click", this._handleFilterToggle);
    }

    document.removeEventListener("click", this._handleGlobalClick);

    if (this.metaobjectsAbortController) {
      this.metaobjectsAbortController.abort();
    }

    clearTimeout(this.sortTimeout);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const gridContainer = document.querySelector(".metaobject-grid-main");
  if (gridContainer) {
    new MetaobjectGridController(gridContainer);
  }
});
